import { Injectable } from '@angular/core';
import { Feature } from 'geojson';
import { Tempo } from '../../models/tempo';
import { MathAux } from '../math-aux/math-aux';

@Injectable({
  providedIn: 'root'
})
export class TimespanService {

  /** Valores limites associados a cada intensidade de chuva, considerando que esses valores estão
   * em milímetros / hora. Se desejar obter os valores limites em outras resoluções de tempo, é
   * necessário dividir os valores limite por um número proporcional. Por exemplo, para obter os valores
   * em milímetros / 15 minutos, deve-se dividir os valores de chuva por 4, pois 15 minutos são 1/4 de
   * uma hora. 
   */
  rainTypeLimit:{[key: string]: number[]} = {'fraca':[0.0001,2.5],'moderada':[2.5,10],'forte':[10,50],'muitoforte':[50,Number.MAX_SAFE_INTEGER]}

  constructor() { }

  /**
   * Calcula o intervalo de tempo entre o primeiro e o último registro de medida de chuva de uma
   * estação específica e entrega uma mensagem formatada com número de dias, horas e minutos.
   *
   * @param first primeiro registro de chuva da estação no período
   * @param last último registro de chuva da estação no período
   * @returns duração da chuva contendo dias, horas e minutos
   */
  createFormattedTimespan(data: any[]) : string {

    let rainTimespan = this.calcTotalRainTimespan(data);
    let message = this.createTimespanMessage(rainTimespan);

    return message;
  }

  /**
   * Calcula o tempo total de duração da chuva no intervalo de tempo fornecido, somando a duração
   * dos intervalos que possuem índice pluviométrico maior que zero.
   *
   * @param data intervalo de tempo ordenado, contendo índices pluviométricos medidos pela estação.
   * @returns array do tempo total de chuva em [ dias, horas, minutos ]
   */
  public calcTotalRainTimespan(data: any[],type?:string) : number[] {
    let i = 0;
    let timeSumInMillis = 0;
    while (i < data.length) {
      if (this.hasRain(data[i].umahora,type)) {
        let j = i + 1;
        while (j < data.length && this.hasRain(data[j].umahora,type)) {
          j++;
        }
        let diff = this.calcDateDiff(data[i], data[j - 1]);
        timeSumInMillis += diff;
        i = j + 1;
      }
      else {
        i++;
      }
    }
    return this.calcTimespan(timeSumInMillis);
  }

  /**
   * Verifica se houve chuva, ou seja, se o valor do índice pluviométrico é maior que zero.
   *
   * @param rainVal valor do índice pluviométrico
   * @returns true se houve chuva; false, caso contrário
   */
  private hasRain(rainVal: number,type?:string) : boolean {
    if (type){
      return (rainVal >= this.rainTypeLimit[type][0] && rainVal < this.rainTypeLimit[type][1])
    } else {
    return rainVal > 0;
    }
  }

    /**
     * Converte a duração ,em milissegundos, para dias, horas e minutos.
     *
     * @param diff diferença de duas datas, em milissegundos.
     * @returns array de valores [ dias, horas, minutos ]
     */
    calcTimespan(diff: number): number[] {

      let mult = this.getTimeMultipliers();
      diff /= mult.seconds;

      let days = Math.floor(diff / mult.days);
      diff -= days * mult.days;

      let hours = Math.floor(diff / mult.hours) % 24;
      diff -= hours * mult.hours;

      let minutes = Math.floor(diff / mult.minutes) % 60;
      diff -= minutes * mult.minutes;

      return [ days, hours, minutes ];
    }

    /**
     * Obtém os multiplicadores usados no cálculo da duração de um intervalo de tempo.
     *
     * @returns
     */
     private getTimeMultipliers() : {[key: string]: number} {

      let seconds = 1000;
      let minutes = 60;
      let hours = minutes * 60;
      let days = hours * 24;

      return { seconds, minutes, hours, days };
    }

    /**
     * Calcula a diferença entre duas datas em milissegundos.
     *
     * @param first primeiro registro de chuva da estação no período.
     * @param last último registro de chuva da estação no período.
     * @returns diferença de tempo dos registros, em milissegundos.
     */
    calcDateDiff(first: any, last: any) : number {

      let firstDate = new Date(first.ano, first.mes - 1, first.dia, first.hora, first.minuto);
      let lastDate = new Date(last.ano, last.mes - 1, last.dia, last.hora, last.minuto);

      return lastDate.valueOf() - firstDate.valueOf();
    }

    /**
     * Cria string de duração da chuva medida pela estação.
     *
     * @param timespan array com quantidade de [ horas, minutos, segundos ]
     * @returns duração da chuva no formato "<num_dias>d <num_horas>h <num_minutos>m"
     */
     private createTimespanMessage(timespan: number[]) : string {

      const units = [ "d", "h", "m" ];
      let message = "";
      units.forEach((unit, index) => {
        let separator = index == units.length - 1 ? "" : " ";
        message += `${timespan[index]}${unit}${separator}`;
      });

      return message;
    }

    /**
     *
     * @param seconds segundos a serem formatados
     * @returns o tempo formatado (dd hh mm)
     */
    secondsToFormattedText(seconds:number): string{
      let milisec = seconds * 1000
      let [dias,horas,minutos] = this.calcTimespan(milisec)


      return `${dias}d ${horas}h ${minutos}m`
    }

    /**
     * Calcula a duração de uma certa intensidade de chuva para uma estação específica. 
     * 
     * @param valuesArray acumulados de chuva da estação.
     * @param dateArray datas associadas aos acumulados de chuva.
     * @param typeOfRain nome da intensidade de chuva
     * @returns duração da intensidade de chuva da estação, em segundos.
     */
    rainDurationCalc(valuesArray:number[],dateArray:Date[],typeOfRain?:string){

      let totalDuration = 0;
      const indexLower = 0;
      const indexUpper = 1;
      const divider15Minutes = 4;

      valuesArray.forEach((value,index)=>{
        if (value > (this.rainTypeLimit[typeOfRain][indexLower]/divider15Minutes) 
          && value < (this.rainTypeLimit[typeOfRain][indexUpper]/divider15Minutes)){

          totalDuration += 15 * 60;

          // if (index == 0) totalDuration += 15 * 60
          // else totalDuration += (dateArray[index].getTime() - dateArray[index-1].getTime())/1000
        }
      })

      return totalDuration
    }

    /**
     * Obtém objeto Date a partir de uma feature com as properties ano, mes, dia, hora e minuto.
     * 
     * @param feature feature que representa um registro de uma estação em um tempo específico.
     * @returns objeto Date criado a partir as properties da estação.
     */
    public getDateFromFeature(feature:Feature<any>){
      let date = new Date(feature.properties.ano,feature.properties.mes - 1,feature.properties.dia,feature.properties.hora,feature.properties.minuto)
      return date
    }

    public tempoToFormattedText(tempo:Tempo){
      let formatedday,formatedhour,formatedmin,formatedmonth
      if (tempo.minuto < 10){formatedmin = "0"+tempo.minuto} else {formatedmin = tempo.minuto}
      if (tempo.hora < 10){formatedhour = "0"+tempo.hora} else {formatedhour = tempo.hora}
      if (tempo.dia < 10){formatedday = "0"+tempo.dia} else {formatedday = tempo.dia}
      if (tempo.mes < 10){formatedmonth = "0"+tempo.mes} else {formatedmonth = tempo.mes}
      return `${formatedhour}:${formatedmin} - ${formatedday}/${formatedmonth}/${tempo.ano}`
    }

    public inputDateToFormattedText(date:string,separator:string="/"){
      let formatedday,formatedmonth,year;
      year =  date.slice(0,4);
      formatedmonth = ("00"+date.slice(5,7)).slice(-2)
      formatedday = ("00"+date.slice(8,10)).slice(-2)

      return `${formatedday}${separator}${formatedmonth}${separator}${year}`
    }

    public formattedDateToDate(date:string){
      let formatedday,formatedmonth,year,formattedHour,formattedMinute;
      year =  parseInt(date.slice(6,10));
      formatedmonth = parseInt(("00"+date.slice(3,5)).slice(-2));
      formatedday = parseInt(("00"+date.slice(0,2)).slice(-2));
      formattedHour = parseInt(("00"+date.slice(11,13)).slice(-2));
      formattedMinute = parseInt(("00"+date.slice(14,16)).slice(-2));

      return new Date(year,formatedmonth-1,formatedday,formattedHour,formattedMinute)
    }

    public static secondsToFormattedText(secs:number){
      let milisec = secs * 1000
      let seconds = 1000;
      let minutes = 60;
      let hours = minutes * 60;
      let days = hours * 24;

      let mult = { seconds, minutes, hours, days };
      milisec /= mult.seconds;

      let dias = Math.floor(milisec / mult.days);
      milisec -= dias * mult.days;

      let horas = Math.floor(milisec / mult.hours) % 24;
      milisec -= horas * mult.hours;

      let minutos = Math.floor(milisec / mult.minutes) % 60;
      milisec -= minutos * mult.minutes;



      return `${dias}d ${horas}h ${minutos}m`
    }

    public formatDatetime(date: Date,separator:string="/") {
      if (date == undefined) return null
      else
      return (
        MathAux.LPAD(date.getDate().toString()) + separator + 
        MathAux.LPAD((date.getMonth()+1).toString()) + separator + 
        date.getFullYear() + " " +
        MathAux.LPAD(date.getHours().toString()) + ":" + 
        MathAux.LPAD(date.getMinutes().toString())
      )
    }

    /**
     * 
     * @param date 
     * @param sep 
     * @param dayFirst 
     * @returns 
     */
    public formatDate(date: Date, sep: string = '-', dayFirst: boolean = false) {

      const LPAD = MathAux.LPAD;

      let year = date.getFullYear();
      let month = LPAD(date.getMonth() + 1);
      let day = LPAD(date.getDate());

      return dayFirst === true ? 
        `${day}${sep}${month}${sep}${year}` :
        `${year}${sep}${month}${sep}${day}`;
    }

    /**
     * 
     * @param date 
     */
    public formatTime(date: Date) {

      const LPAD = MathAux.LPAD;

      let hours = LPAD(date.getHours());
      let minutes = LPAD(date.getMinutes());

      return `${hours}:${minutes}`;
    }

    public tempoToDate(tempo: Tempo){

      return new Date(tempo.ano,tempo.mes-1,tempo.dia,tempo.hora,tempo.minuto);
    }


}
